import { Download } from "lucide-react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import type { ComponentProps } from "react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Modal } from "@/components/ui/modal";
import { cn } from "@/lib/utils/cn";

import {
  downloadExpertXlsx,
  useAdminCountsQuery,
  useAdminExpertsQuery,
  useAdminInvitationsQuery,
  useResendInvite,
  useRevokeInvite,
  useSendInvite,
  useSendPendingInvites,
  useServiceRegionsQuery,
} from "../../api/hooks";
import { useIssues } from "@/features/questionnaire/api";
import type { AdminExpertRow, ExpertInvitation } from "../../types";
import { Badge } from "../ui/Badge";
import { Tabs } from "../ui/Tabs";
import { ErrorState, Loading } from "../ui/states";
import { AdminExpertDetail } from "./AdminExpertDetail";
import { AdminImport } from "./AdminImport";

const STATUS_TONE = {
  draft: "neutral",
  // Solid, unlike its neighbours — the Django list singles "Soumis" out with
  // `.badge-orange` for the same reason: it is the row an admin is looking for.
  submitted: "attention",
  validated: "success",
  rejected: "destructive",
} as const;

const ORIGINS = ["invitation", "import", "self_registration"] as const;

const selectClass =
  "h-10 w-full min-w-0 rounded-md border border-input bg-background px-3 text-sm";

type View =
  | { name: "list" }
  | { name: "detail"; id: string }
  | { name: "import"; kind: "experts" | "issues" };

type AdminTab = "experts" | "invitations";

/** "Experts (10)" — the count is omitted until it has loaded, never shown as 0. */
function withCount(label: string, count: number | undefined) {
  return count === undefined ? label : `${label} (${count})`;
}

/** CQDD expert back-office: list + review + invitations + imports + export. */
export function AdminApp() {
  const { t } = useTranslation();
  const [view, setView] = useState<View>({ name: "list" });
  // The tab lives in `?tab=` so a reload (or a shared link) lands where you were,
  // matching the Django list. Filters below stay component state, so switching
  // tabs — or filtering within one — never loses the other's search.
  const search = useSearch({ strict: false }) as { tab?: AdminTab };
  const navigate = useNavigate();
  const counts = useAdminCountsQuery();
  const tab: AdminTab = search.tab === "invitations" ? "invitations" : "experts";
  const setTab = (next: AdminTab) =>
    void navigate({
      to: "/experts/admin",
      search: next === "experts" ? {} : { tab: next },
      replace: true,
    });

  if (view.name === "detail") {
    return (
      <AdminExpertDetail id={view.id} onBack={() => setView({ name: "list" })} />
    );
  }
  if (view.name === "import") {
    return (
      <div className="space-y-4">
        <Button variant="ghost" onClick={() => setView({ name: "list" })}>
          ← {t("expert.admin.title")}
        </Button>
        <AdminImport kind={view.kind} />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold">{t("expert.admin.title")}</h1>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setView({ name: "import", kind: "experts" })}
          >
            {t("expert.admin.importExperts")}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setView({ name: "import", kind: "issues" })}
          >
            {t("expert.admin.importIssues")}
          </Button>
        </div>
      </div>

      <Tabs
        items={[
          {
            value: "experts",
            label: withCount(t("expert.admin.tabs.experts"), counts.data?.experts),
          },
          {
            value: "invitations",
            label: withCount(
              t("expert.admin.tabs.invitations"),
              counts.data?.pending_invitations,
            ),
          },
        ]}
        value={tab}
        onChange={setTab}
        label={t("expert.admin.tabsLabel")}
      />

      {tab === "experts" ? (
        <ExpertList onOpen={(id) => setView({ name: "detail", id })} />
      ) : (
        <InvitationsSection />
      )}
    </div>
  );
}

function ExpertList({ onOpen }: { onOpen: (id: string) => void }) {
  const { t } = useTranslation();
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [region, setRegion] = useState("");
  const [origin, setOrigin] = useState("");
  const [issue, setIssue] = useState("");
  const [pending, setPending] = useState(false);
  const [page, setPage] = useState(1);

  const regions = useServiceRegionsQuery();
  // Every enjeu, not the registration catalogue: that one only lists enjeux an
  // expert can pick, so filtering the back-office by it would hide exactly the
  // experts this branch surfaces — the ones holding a pre-approval on an enjeu
  // registration doesn't offer. Django's ExpertFilterSet.issue uses Issue.objects
  // for the same reason.
  const allIssues = useIssues();
  const issues = useMemo(
    () =>
      [...(allIssues.data ?? [])]
        .map((i) => ({ id: i.id, name: i.name }))
        .sort((a, b) => a.name.localeCompare(b.name, "fr")),
    [allIssues.data],
  );

  // Live filters (params drive the query); Reset clears them all.
  const params: Record<string, string> = { page: String(page) };
  if (q) params.q = q;
  if (status) params.status = status;
  if (region) params.service_regions = region;
  if (origin) params.invited_source = origin;
  if (issue) params.issue = issue;
  if (pending) params.pending_changes = "true";
  const query = useAdminExpertsQuery(params);

  const hasFilters =
    Boolean(q || status || region || origin || issue) || pending;
  const reset = () => {
    setQ("");
    setStatus("");
    setRegion("");
    setOrigin("");
    setIssue("");
    setPending(false);
    setPage(1);
  };
  const bump =
    <T,>(setter: (v: T) => void) =>
    (v: T) => {
      setter(v);
      setPage(1);
    };

  const exportUrl = (all: boolean) => {
    const p = new URLSearchParams(all ? { all: "1" } : params);
    p.delete("page");
    return `/api/v1/experts/admin/export/?${p.toString()}`;
  };

  return (
    <section className="space-y-3">
      {/* Filter section — the shared Administration pattern (see
          docs/frontend-styling-guidelines.md → "Filter section"): a card whose
          top row holds the FILTERS label, Reset, and the export button(s) — then
          a full-width search, a control grid, and the pending-changes facet. */}
      <Card>
        <CardContent className="space-y-3 p-4">
          {/* Top row: label + reset + export (export lives here, no dead gap).
              The list exports two ways once filtered (filtered vs. all). */}
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
              {t("expert.admin.filtersHeading")}
            </span>
            <span className="flex-1" />
            {hasFilters && (
              <button
                type="button"
                onClick={reset}
                className="text-xs font-semibold text-primary hover:underline"
              >
                {t("expert.admin.reset")}
              </button>
            )}
            {hasFilters ? (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    void downloadExpertXlsx(exportUrl(false), "experts.xlsx")
                  }
                >
                  <Download aria-hidden />
                  {t("expert.admin.exportFiltered")}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    void downloadExpertXlsx(exportUrl(true), "experts.xlsx")
                  }
                >
                  <Download aria-hidden />
                  {t("expert.admin.exportAll")}
                </Button>
              </>
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  void downloadExpertXlsx(
                    "/api/v1/experts/admin/export/",
                    "experts.xlsx",
                  )
                }
              >
                <Download aria-hidden />
                {t("expert.admin.exportButton")}
              </Button>
            )}
          </div>

          <Input
            value={q}
            onChange={(e) => bump(setQ)(e.target.value)}
            placeholder={t("expert.admin.search")}
          />

          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
            <select
              value={status}
              onChange={(e) => bump(setStatus)(e.target.value)}
              className={selectClass}
            >
              <option value="">{t("expert.admin.allStatuses")}</option>
              {(["draft", "submitted", "validated", "rejected"] as const).map((s) => (
                <option key={s} value={s}>
                  {t(`expert.admin.statuses.${s}`)}
                </option>
              ))}
            </select>
            <select
              value={region}
              onChange={(e) => bump(setRegion)(e.target.value)}
              className={selectClass}
            >
              <option value="">{t("expert.admin.allRegions")}</option>
              {(regions.data ?? []).map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </select>
            <select
              value={origin}
              onChange={(e) => bump(setOrigin)(e.target.value)}
              className={selectClass}
            >
              <option value="">{t("expert.admin.allProvenances")}</option>
              {ORIGINS.map((o) => (
                <option key={o} value={o}>
                  {t(`expert.invitations.source.${o}`)}
                </option>
              ))}
            </select>
            <select
              value={issue}
              onChange={(e) => bump(setIssue)(e.target.value)}
              className={selectClass}
            >
              <option value="">{t("expert.admin.allIssues")}</option>
              {issues.map((i) => (
                <option key={i.id} value={i.id}>
                  {i.name}
                </option>
              ))}
            </select>
          </div>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={pending}
              onChange={(e) => bump(setPending)(e.target.checked)}
              className="size-4 rounded border-input text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
            {t("expert.admin.pendingChanges")}
          </label>
        </CardContent>
      </Card>

      {/* The total that follows the filter selection, as on the Django list. The
          paginated response's count is the whole filtered set, not just the page. */}
      {query.data && (
        <p className="text-sm text-muted-foreground">
          {t(
            hasFilters ? "expert.admin.countMatch" : "expert.admin.countTotal",
            { count: query.data.count },
          )}
        </p>
      )}

      {query.isPending ? (
        <Loading />
      ) : query.isError || !query.data ? (
        <ErrorState message={t("expert.error")} />
      ) : query.data.results.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t("expert.admin.empty")}</p>
      ) : (
        <>
        {/* Mobile: one card per expert; the 6-column table squishes on a phone. */}
        <ul className="space-y-2 md:hidden">
          {query.data.results.map((row: AdminExpertRow) => (
            <li key={row.id} className="rounded-lg border p-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-medium">
                    {row.organization_name || (
                      <span className="italic text-muted-foreground">
                        {t("expert.admin.noName")}
                      </span>
                    )}
                  </div>
                  <div className="break-words text-xs text-muted-foreground">
                    {row.email}
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => onOpen(row.id)}>
                  {t("expert.admin.view")}
                </Button>
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <Badge tone={STATUS_TONE[row.status]}>
                  {t(`expert.admin.statuses.${row.status}`)}
                </Badge>
                {row.has_pending_changes && (
                  <Badge tone="warning">{t("expert.admin.pendingChanges")}</Badge>
                )}
              </div>
              <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm">
                <dt className="text-muted-foreground">
                  {t("expert.admin.columns.registered")}
                </dt>
                <dd className="tabular-nums">
                  {row.created_at ? row.created_at.slice(0, 10) : "—"}
                </dd>
                <dt className="text-muted-foreground">
                  {t("expert.admin.columns.lastLogin")}
                </dt>
                <dd className="tabular-nums">
                  {row.last_login
                    ? row.last_login.slice(0, 16).replace("T", " ")
                    : "—"}
                </dd>
                <dt className="text-muted-foreground">
                  {t("expert.admin.columns.actionSelections")}
                </dt>
                <dd className="tabular-nums">{row.action_selection_count}</dd>
              </dl>
            </li>
          ))}
        </ul>

        <div className="hidden overflow-x-auto rounded-lg border md:block">
          <table className="w-full text-sm">
            <thead className="bg-secondary/50 text-left">
              <tr>
                <th className="px-3 py-2">{t("expert.admin.columns.organization")}</th>
                <th className="px-3 py-2">{t("expert.admin.columns.email")}</th>
                <th className="px-3 py-2">{t("expert.admin.columns.status")}</th>
                <th className="px-3 py-2">{t("expert.admin.columns.registered")}</th>
                <th className="px-3 py-2">{t("expert.admin.columns.lastLogin")}</th>
                <th className="px-3 py-2">
                  {t("expert.admin.columns.actionSelections")}
                </th>
                <th className="px-3 py-2">{t("expert.admin.columns.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {query.data.results.map((row: AdminExpertRow) => (
                <tr key={row.id} className="border-t">
                  <td className="px-3 py-2 font-medium">
                    {row.organization_name || (
                      <span className="italic text-muted-foreground">
                        {t("expert.admin.noName")}
                      </span>
                    )}
                    {row.has_pending_changes && (
                      <Badge tone="warning" className="ml-2">
                        {t("expert.admin.pendingChanges")}
                      </Badge>
                    )}
                  </td>
                  <td className="px-3 py-2 text-muted-foreground">{row.email}</td>
                  <td className="px-3 py-2">
                    <Badge tone={STATUS_TONE[row.status]}>
                      {t(`expert.admin.statuses.${row.status}`)}
                    </Badge>
                  </td>
                  <td className="px-3 py-2 tabular-nums text-muted-foreground">
                    {row.created_at ? row.created_at.slice(0, 10) : "—"}
                  </td>
                  <td className="px-3 py-2 tabular-nums text-muted-foreground">
                    {row.last_login
                      ? row.last_login.slice(0, 16).replace("T", " ")
                      : "—"}
                  </td>
                  <td className="px-3 py-2 tabular-nums text-muted-foreground">
                    {row.action_selection_count}
                  </td>
                  <td className="px-3 py-2">
                    <Button variant="ghost" size="sm" onClick={() => onOpen(row.id)}>
                      {t("expert.admin.view")}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </>
      )}

      {query.data && (query.data.next || query.data.previous) && (
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            disabled={!query.data.previous}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            ←
          </Button>
          <span className="text-sm text-muted-foreground tabular-nums">{page}</span>
          <Button
            variant="outline"
            size="sm"
            disabled={!query.data.next}
            onClick={() => setPage((p) => p + 1)}
          >
            →
          </Button>
        </div>
      )}
    </section>
  );
}

type InvitationStatus = "revoked" | "used" | "expired" | "sent" | "unsent";

function invitationStatus(inv: ExpertInvitation): InvitationStatus {
  if (inv.is_revoked) return "revoked";
  if (inv.is_used) return "used";
  if (inv.is_expired) return "expired";
  if (inv.is_sent) return "sent";
  return "unsent";
}

/** Mirrors the Django badge colours on the invitations table. */
const INVITATION_TONE: Record<
  InvitationStatus,
  ComponentProps<typeof Badge>["tone"]
> = {
  revoked: "destructive",
  used: "success",
  expired: "destructive",
  sent: "info",
  unsent: "neutral",
};

function InvitationsSection() {
  const { t } = useTranslation();
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const params: Record<string, string> = { page: String(page) };
  if (q) params.q = q;
  if (status) params.status = status;

  const query = useAdminInvitationsQuery(params);
  const sendInvite = useSendInvite();
  const resend = useResendInvite();
  const revoke = useRevokeInvite();
  const sendPending = useSendPendingInvites();
  const [email, setEmail] = useState("");
  const [contactName, setContactName] = useState("");
  const [language, setLanguage] = useState<"fr" | "en">("fr");
  const [inviteOpen, setInviteOpen] = useState(false);

  const fmt = (iso: string | null | undefined) =>
    iso ? iso.slice(0, 16).replace("T", " ") : "—";

  const totalPages = query.data ? Math.max(1, query.data.num_pages) : 1;

  // Shared between the desktop table and the mobile cards. Kept on one line:
  // a list mixing "Envoyée"/"Non envoyée" widens the status column, which
  // otherwise squeezes this cell until the two buttons wrap.
  const renderInvActions = (inv: ExpertInvitation) => (
    <div className="flex items-center gap-2 whitespace-nowrap">
      <Button
        variant="ghost"
        size="sm"
        onClick={() =>
          resend.mutate(inv.id, {
            onSuccess: () =>
              toast.success(
                t(inv.is_sent ? "expert.admin.resend" : "expert.admin.send"),
              ),
            onError: () => toast.error(t("expert.error")),
          })
        }
        disabled={resend.isPending}
      >
        {inv.is_sent ? t("expert.admin.resend") : t("expert.admin.send")}
      </Button>
      <Button
        variant="ghost"
        size="sm"
        className="text-destructive hover:text-destructive"
        onClick={() => {
          if (!window.confirm(t("expert.admin.confirmRevoke"))) return;
          revoke.mutate(inv.id, {
            onSuccess: () => toast.success(t("expert.admin.revoke")),
            onError: () => toast.error(t("expert.error")),
          });
        }}
        disabled={revoke.isPending}
      >
        {t("expert.admin.revoke")}
      </Button>
    </div>
  );

  return (
    <section className="space-y-4">
      {/* No heading: the tab above already names this section (parity with the
          Django list, which dropped its own heading for the same reason). */}
      <div className="flex flex-wrap items-center justify-end gap-3">
        <div className="flex flex-wrap items-center gap-2">
          {query.data && query.data.unsent_count > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                if (!window.confirm(t("expert.admin.confirmBulkSend"))) return;
                sendPending.mutate(undefined, {
                  onSuccess: () => toast.success(t("expert.admin.sendPending", { count: query.data!.unsent_count })),
                  onError: () => toast.error(t("expert.error")),
                });
              }}
              disabled={sendPending.isPending}
            >
              {t("expert.admin.sendPending", { count: query.data.unsent_count })}
            </Button>
          )}
          <Button size="sm" onClick={() => setInviteOpen(true)}>
            {t("expert.admin.sendInvite")}
          </Button>
        </div>
      </div>

      {/* invite a single expert */}
      {inviteOpen && (
        <Modal
          title={t("expert.admin.sendInvite")}
          onClose={() => setInviteOpen(false)}
          className="max-w-md"
        >
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const to = email.trim();
              if (!to) return;
              sendInvite.mutate(
                { email: to, contact_name: contactName.trim(), language },
                {
                  onSuccess: () => {
                    // Django's flash after the same send.
                    toast.success(t("expert.admin.inviteSent", { email: to }));
                    setEmail("");
                    setContactName("");
                    setInviteOpen(false);
                  },
                  onError: () => toast.error(t("expert.error")),
                },
              );
            }}
            className="space-y-4"
          >
            <div className="space-y-1.5">
              <label htmlFor="invite-email" className="block text-sm font-medium">
                <span className="mr-0.5 text-primary">*</span>
                {t("expert.admin.inviteEmail")}
              </label>
              <Input
                id="invite-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <label htmlFor="invite-contact-name" className="block text-sm font-medium">
                {t("expert.admin.inviteContactName")}
              </label>
              <Input
                id="invite-contact-name"
                maxLength={255}
                value={contactName}
                onChange={(e) => setContactName(e.target.value)}
                autoComplete="off"
              />
            </div>
            <div className="space-y-1.5">
              {/* `block` on the label is load-bearing: a bare <label> is inline,
                  so with a narrow (w-28) control they'd share one line. */}
              <label htmlFor="invite-language" className="block text-sm font-medium">
                {t("expert.admin.language")}
              </label>
              <select
                id="invite-language"
                value={language}
                onChange={(e) => setLanguage(e.target.value as "fr" | "en")}
                className={cn(selectClass, "w-28")}
              >
                <option value="fr">FR</option>
                <option value="en">EN</option>
              </select>
            </div>
            <div className="flex justify-end">
              <Button type="submit" disabled={sendInvite.isPending}>
                {t("expert.admin.sendInviteSubmit")}
              </Button>
            </div>
          </form>
        </Modal>
      )}

      {/* Invitations keep a compact inline search + status filter — deliberately
          NOT the full filter-card used for the expert list above. */}
      <div className="flex flex-wrap items-center gap-3 rounded-lg border bg-secondary/30 p-3">
        <Input
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setPage(1);
          }}
          placeholder={t("expert.admin.searchInvitation")}
          className="min-w-0 flex-1"
        />
        <select
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setPage(1);
          }}
          className={cn(selectClass, "w-full sm:w-48")}
        >
          <option value="">{t("expert.admin.invAllStatuses")}</option>
          <option value="unsent">{t("expert.admin.invStatusUnsent")}</option>
          <option value="sent">{t("expert.admin.invStatusSent")}</option>
          <option value="expired">{t("expert.admin.invStatusExpired")}</option>
        </select>
      </div>

      {query.isPending ? (
        <Loading />
      ) : !query.data || query.data.results.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {t("expert.admin.invitationEmpty")}
        </p>
      ) : (
        <>
        {/* Mobile: one card per invitation; the table squishes on a phone. */}
        <ul className="space-y-2 md:hidden">
          {query.data.results.map((inv) => {
            const st = invitationStatus(inv);
            return (
              <li
                key={inv.id}
                className={cn(
                  "rounded-lg border p-3",
                  inv.is_expired && "bg-destructive/5",
                )}
              >
                <div className="break-words font-medium">{inv.email}</div>
                <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm">
                  <dt className="text-muted-foreground">
                    {t("expert.invitations.columns.status")}
                  </dt>
                  <dd>
                    <Badge tone={INVITATION_TONE[st]}>
                      {t(`expert.invitations.status.${st}`)}
                    </Badge>
                  </dd>
                  <dt className="text-muted-foreground">{t("expert.admin.invSentAt")}</dt>
                  <dd className="tabular-nums">{fmt(inv.invitation_sent_at)}</dd>
                  <dt className="text-muted-foreground">{t("expert.admin.invExpiresAt")}</dt>
                  <dd className="tabular-nums">{fmt(inv.expires_at)}</dd>
                </dl>
                <div className="mt-3">{renderInvActions(inv)}</div>
              </li>
            );
          })}
        </ul>

        <div className="hidden overflow-x-auto rounded-lg border md:block">
          <table className="w-full text-sm">
            <thead className="bg-secondary/50 text-left">
              <tr>
                <th className="px-3 py-2">{t("expert.invitations.columns.email")}</th>
                <th className="px-3 py-2">{t("expert.invitations.columns.status")}</th>
                <th className="px-3 py-2">{t("expert.admin.invSentAt")}</th>
                <th className="px-3 py-2">{t("expert.admin.invExpiresAt")}</th>
                <th className="px-3 py-2">{t("expert.admin.columns.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {query.data.results.map((inv) => {
                const st = invitationStatus(inv);
                return (
                  <tr
                    key={inv.id}
                    className={inv.is_expired ? "border-t bg-destructive/5" : "border-t"}
                  >
                    <td className="px-3 py-2">{inv.email}</td>
                    <td className="whitespace-nowrap px-3 py-2">
                      <Badge tone={INVITATION_TONE[st]}>
                        {t(`expert.invitations.status.${st}`)}
                      </Badge>
                    </td>
                    <td className="px-3 py-2 tabular-nums text-muted-foreground">
                      {fmt(inv.invitation_sent_at)}
                    </td>
                    <td className="px-3 py-2 tabular-nums text-muted-foreground">
                      {fmt(inv.expires_at)}
                    </td>
                    <td className="px-3 py-2">{renderInvActions(inv)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        </>
      )}

      {query.data && totalPages > 1 && (
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            ←
          </Button>
          <span className="text-sm text-muted-foreground tabular-nums">
            {page} / {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            →
          </Button>
        </div>
      )}
    </section>
  );
}
